import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';
import { AuthService } from 'src/app/_services/auth.service';

export type WebhookResponseType = 'Success' | 'Fail';

@Component({
  selector: 'app-webhook-test-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './webhook-test-dialog.component.html',
  styleUrls: ['./webhook-test-dialog.component.css']
})
export class WebhookTestDialogComponent implements OnChanges, AfterViewInit {
  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly authService: AuthService
  ) {}

  @Input() isVisible: boolean = false;
  @Input() eventType: string = '';
  @Input() merchantId: string = '';
  /** Configured webhook URL for this event type (Sale / Refund) */
  @Input() webhookUrl: string = '';

  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() testResult = new EventEmitter<{ success: boolean; status: number; message: string }>();

  @ViewChild('curlTextarea') curlTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('lineNumbers') lineNumbers!: ElementRef<HTMLDivElement>;
  @ViewChild('highlight') highlight!: ElementRef<HTMLDivElement>;
  @ViewChild('responseDropdown') responseDropdown!: ElementRef<HTMLDivElement>;

  curlCommand: string = '';
  isExecuting: boolean = false;
  lastRequestedTime: string = '';
  lineNumbersArray: number[] = [1];
  highlightedContent: SafeHtml = '';

  readonly responseTypeOptions: WebhookResponseType[] = ['Success', 'Fail'];
  selectedResponseType: WebhookResponseType = 'Success';
  isResponseDropdownOpen: boolean = false;

  toggleResponseDropdown(): void {
    this.isResponseDropdownOpen = !this.isResponseDropdownOpen;
  }

  selectResponseType(type: WebhookResponseType): void {
    this.selectedResponseType = type;
    this.isResponseDropdownOpen = false;
    this.updateCurlPreview();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isResponseDropdownOpen && this.responseDropdown &&
        !this.responseDropdown.nativeElement.contains(event.target as Node)) {
      this.isResponseDropdownOpen = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      if (this.isVisible) {
        this.initializeDialog();
      } else {
        // Clear UI state when dialog closes to prevent ghosting
        this.clearUIState();
      }
      return;
    }

    if (this.isVisible && (changes['webhookUrl'] || changes['eventType'])) {
      this.updateCurlPreview();
    }
  }

  ngAfterViewInit(): void {
    this.updateLineNumbers();
    this.updateHighlightedContent();
  }

  private initializeDialog(): void {
    this.isExecuting = false;
    this.selectedResponseType = 'Success';
    this.isResponseDropdownOpen = false;
    this.updateCurlPreview();
    this.updateLastRequestedTime();
    // Force update UI to prevent ghosting when reopening
    setTimeout(() => {
      this.updateLineNumbers();
      this.updateHighlightedContent();
    }, 0);
  }

  private clearUIState(): void {
    // Reset scroll positions to prevent ghosting
    if (this.curlTextarea?.nativeElement) {
      this.curlTextarea.nativeElement.scrollTop = 0;
      this.curlTextarea.nativeElement.scrollLeft = 0;
    }
    if (this.lineNumbers?.nativeElement) {
      this.lineNumbers.nativeElement.scrollTop = 0;
    }
    if (this.highlight?.nativeElement) {
      this.highlight.nativeElement.style.transform = 'translate(0px, 0px)';
    }
  }

  private static readonly RESPONSE_BODY_MAP: Record<'Sale' | 'Refund', Record<WebhookResponseType, object>> = {
    Sale: {
      Success: {
        PG_REF_NUM: '2601142428658552',
        TXN_ID: '2601142524215561',
        RRN: '2601142524215562',
        ORDER_ID: '20260701_142428',
        TXNTYPE: 'SALE',
        CUST_NAME: null,
        CUST_EMAIL: 'ckkadam1999@gmail.com',
        STATUS: 'Captured',
        PAYMENT_TYPE: 'PTQR',
        MOP_TYPE: 'PAYQR',
        AMOUNT: '1.00',
        TOTAL_AMOUNT: '1.00',
        CREATE_DATE: '2026-07-01 14:25:24',
        UPDATE_DATE: '2026-07-01 14:25:24',
        PAY_ID: '12345678',
        HASH: 'A4B2DE730AFD887A29076B239C6AE03CDA812AF99933F5224E862508DDA5A2CD'
      },
      Fail: {
        PG_REF_NUM: '2601150434456037',
        TXN_ID: '2601151328123246',
        ORDER_ID: '20260701_150434',
        TXNTYPE: 'SALE',
        CUST_NAME: null,
        CUST_EMAIL: 'ckkadam1999@gmail.com',
        STATUS: 'Failed',
        PAYMENT_TYPE: 'PTQR',
        MOP_TYPE: 'PAYQR',
        AMOUNT: '25.00',
        TOTAL_AMOUNT: '25.00',
        CREATE_DATE: '2026-07-01 15:13:28',
        UPDATE_DATE: '2026-07-01 15:13:28',
        PAY_ID: '12345678',
        HASH: 'B8D951C057AA721379DF38FCF3B5425DD9C5885431A070CF1D2839916A783CBA'
      }
    },
    Refund: {
      Success: {
        PG_REF_NUM: '2624110532023023',
        TXN_ID: '2601143700066659',
        RRN: '2601142524215562',
        ORDER_ID: '20260624_110531',
        REFUND_ORDER_ID: '20260624_110532',
        TXNTYPE: 'REFUND',
        CUST_NAME: null,
        CUST_EMAIL: 'ckkadam1999@gmail.com',
        STATUS: 'Captured',
        PAYMENT_TYPE: 'PTQR',
        MOP_TYPE: 'PAYQR',
        AMOUNT: '1.00',
        TOTAL_AMOUNT: '100.00',
        CREATE_DATE: '2026-07-01 14:37:07',
        UPDATE_DATE: '2026-07-01 14:37:07',
        PAY_ID: '12345678',
        HASH: 'AEEF001614AAAF44724FB7039B9C9170B2E48EDDC37BD0DB824931177C7914DB'
      },
      Fail: {
        PG_REF_NUM: '2624110532023023',
        TXN_ID: '2601143700066659',
        RRN: '2601142524215562',
        ORDER_ID: '20260624_110531',
        REFUND_ORDER_ID: '20260624_110532',
        TXNTYPE: 'REFUND',
        CUST_NAME: null,
        CUST_EMAIL: 'ckkadam1999@gmail.com',
        STATUS: 'Failed',
        PAYMENT_TYPE: 'PTQR',
        MOP_TYPE: 'PAYQR',
        AMOUNT: '1.00',
        TOTAL_AMOUNT: '100.00',
        CREATE_DATE: '2026-07-01 14:37:07',
        UPDATE_DATE: '2026-07-01 14:37:07',
        PAY_ID: '12345678',
        HASH: 'AEEF001614AAAF44724FB7039B9C9170B2E48EDDC37BD0DB824931177C7914DB'
      }
    }
  };

  private getResponseBody(): object {
    const eventKey: 'Sale' | 'Refund' = this.eventType?.toLowerCase() === 'refund' ? 'Refund' : 'Sale';
    return WebhookTestDialogComponent.RESPONSE_BODY_MAP[eventKey][this.selectedResponseType];
  }

  /** Renders the request that sendTestNotification() will perform, as a copyable curl command */
  private buildCurlCommand(): string {
    const url = (this.webhookUrl || '').trim() || '<no webhook URL configured>';
    const body = JSON.stringify(this.getResponseBody(), null, 2);
    return [
      `curl --location '${url}' \\`,
      `--header 'Content-Type: application/json' \\`,
      `--data '${body}'`
    ].join('\n');
  }

  private updateCurlPreview(): void {
    this.curlCommand = this.buildCurlCommand();
    this.onCurlCommandChange();
  }


  private updateLastRequestedTime(): void {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    this.lastRequestedTime = `${day}/${month}/${year} at ${hours}:${minutes}:${seconds}`;
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  get canSend(): boolean {
    return !this.isExecuting && !!this.webhookUrl?.trim() && !!this.curlCommand?.trim();
  }

  sendTestNotification(): void {
    if (!this.canSend) return;

    this.isExecuting = true;
    this.updateLastRequestedTime();

    const webhookUrl = (this.webhookUrl || '').trim();
    const body = this.getResponseBody();

    console.debug('Sending test webhook via wrapper API:', webhookUrl, 'Body:', body);

    this.authService.testWebhookNotification(webhookUrl, body)
      .subscribe({
        next: response => {
          this.isExecuting = false;
          console.debug('Test webhook success:', response);
          this.close();
          this.testResult.emit({ success: true, status: response.status, message: 'Test notification sent successfully' });
        },
        error: (error: HttpErrorResponse) => {
          this.isExecuting = false;
          console.error('Test webhook error:', error);
          this.close();
          this.testResult.emit({
            success: false,
            status: error.status,
            message: parseHttpErrorMessage(error, 'Failed to send test notification')
          });
        }
      });
  }

  onCurlCommandChange(): void {
    this.updateLineNumbers();
    this.updateHighlightedContent();
  }

  onTextareaScroll(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (this.lineNumbers) {
      this.lineNumbers.nativeElement.scrollTop = textarea.scrollTop;
    }
    // Sync highlight overlay with textarea scroll (both axes)
    if (this.highlight) {
      const x = -textarea.scrollLeft;
      const y = -textarea.scrollTop;
      this.highlight.nativeElement.style.transform = `translate(${x}px, ${y}px)`;
    }
  }

  private updateLineNumbers(): void {
    const lines = this.curlCommand.split('\n').length;
    this.lineNumbersArray = Array.from({ length: Math.max(lines, 1) }, (_, i) => i + 1);
  }

  private updateHighlightedContent(): void {
    if (!this.curlCommand) {
      this.highlightedContent = this.sanitizer.bypassSecurityTrustHtml('');
      return;
    }

    // Split by lines and highlight each line
    const lines = this.curlCommand.split('\n');
    const curlRegex = /^(\s*)(curl\b)/i;
    const highlightedLines = lines.map(line => {
      // Check if line starts with 'curl'
      const curlMatch = curlRegex.exec(line);
      if (curlMatch) {
        const spaces = curlMatch[1];
        const curlWord = curlMatch[2];
        const rest = line.substring(curlMatch[0].length);
        return `${this.escapeHtml(spaces)}<span class="curl-keyword">${this.escapeHtml(curlWord)}</span><span class="curl-rest">${this.escapeHtml(rest)}</span>`;
      } else {
        return `<span class="curl-rest">${this.escapeHtml(line)}</span>`;
      }
    });

    const highlighted = highlightedLines.join('\n');
    this.highlightedContent = this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
